import { useCallback, useEffect, useRef, useState } from "react";
import closemodal from "../../../../src/assets/images/close.svg";
import useLanguageTranslate from "../../../hooks/useLanguageTranslate";
import "./modal.css";

// Fallback in case animationend never fires (e.g., display:none parent).
// Comfortably longer than the longest exit, the 220ms mobile slide-down, so it
// never trims one that is still playing.
const CLOSE_ANIM_FALLBACK_MS = 300;

const prefersReducedMotion = () => {
  if (typeof window === "undefined" || !window.matchMedia) return false;
  try {
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  } catch (e) {
    return false;
  }
};

const BackdropModal = ({
  children,
  className,
  contentClassName,
  onClose,
  show,
  title,
  headerClassName,
  emergencyServiceData,
  hideClosebtn = false,
  fullPageOnMobile = false,
}) => {
  const translate = useLanguageTranslate();
  const [isMounted, setIsMounted] = useState(show);
  const [isClosing, setIsClosing] = useState(false);
  const contentRef = useRef(null);
  const closeTimerRef = useRef(null);
  const pendingActionRef = useRef(null); // "unmount" | "callback"
  // One-shot guard: when the X-driven exit animation has already played,
  // we skip the next animation when the parent flips show=false (otherwise
  // the .is-closing class would restart the keyframe and cause a flicker).
  const skipNextCloseAnimRef = useRef(false);

  const clearCloseTimer = () => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  };

  const runPendingAction = useCallback(() => {
    const action = pendingActionRef.current;
    pendingActionRef.current = null;
    clearCloseTimer();
    setIsClosing(false);
    if (action === "unmount") {
      setIsMounted(false);
    } else if (action === "callback") {
      // The X-driven exit animation just finished. When the parent reacts
      // by flipping show=false, the useEffect must NOT re-play the exit
      // animation (that re-triggers the keyframe from its start frame and
      // causes a visible flicker). The flag is consumed in the effect.
      skipNextCloseAnimRef.current = true;
      if (typeof onClose === "function") onClose();
    }
  }, [onClose]);

  useEffect(() => {
    if (show) {
      setIsMounted(true);
      setIsClosing(false);
      pendingActionRef.current = null;
      skipNextCloseAnimRef.current = false;
      clearCloseTimer();
    } else if (isMounted) {
      // Parent set show=false. If the X-driven exit animation already played,
      // consume the one-shot guard and unmount immediately — no second animation.
      if (skipNextCloseAnimRef.current) {
        skipNextCloseAnimRef.current = false;
        pendingActionRef.current = null;
        clearCloseTimer();
        setIsClosing(false);
        setIsMounted(false);
        return;
      }
      // Otherwise play exit animation, then unmount.
      if (prefersReducedMotion()) {
        setIsMounted(false);
        return;
      }
      pendingActionRef.current = "unmount";
      setIsClosing(true);
      clearCloseTimer();
      closeTimerRef.current = setTimeout(runPendingAction, CLOSE_ANIM_FALLBACK_MS);
    }
    return clearCloseTimer;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);

  const handleClose = useCallback(() => {
    if (isClosing) return;
    if (prefersReducedMotion()) {
      if (typeof onClose === "function") onClose();
      return;
    }
    pendingActionRef.current = "callback";
    setIsClosing(true);
    clearCloseTimer();
    closeTimerRef.current = setTimeout(runPendingAction, CLOSE_ANIM_FALLBACK_MS);
  }, [isClosing, onClose, runPendingAction]);

  const handleAnimationEnd = useCallback(
    (e) => {
      // Only react to the content's own exit animation, not children's.
      if (e.target !== contentRef.current) return;
      if (!isClosing) return;
      runPendingAction();
    },
    [isClosing, runPendingAction]
  );

  if (!isMounted) return null;

  const closingClass = isClosing ? "is-closing" : "";

  return (
    <div className={`backdropModal  ${className || ""}`}>
      <div
        className={`solidarity-modal-backdrop ${closingClass} fixed inset-0 bg-black bg-opacity-50 z-[51] ${fullPageOnMobile ? "max767:hidden" : ""}`}
      ></div>
      <div className={`fixed inset-0 flex items-center ${fullPageOnMobile ? "max767:items-stretch" : "max767:items-end"} justify-around z-[52] p-4 max767:p-0 overflow-y-auto max767:z-[99999]`}>
        <div
          onClick={handleClose}
          className="backdrop absolute z-9 w-full h-full"
        ></div>

        <div
          ref={contentRef}
          onAnimationEnd={handleAnimationEnd}
          className={`solidarity-modal-content ${closingClass} z-10 bg-white rounded-lg max767:!rounded-b-none pt-5 pb-6 px-6 w-auto new-policy-modal max767:w-full max767:overflow-y-auto ${fullPageOnMobile ? "max767:!h-screen max767:!max-h-screen max767:!rounded-none max767:!top-0 max767:!pb-32" : "max767:max-h-[90vh] max767:h-auto max767:top-[8%]"} ipad:w-[85%] ipad:mt-[1%] ${contentClassName || ""}`}
        >
          <div
            className={`flex close-modal-icon justify-end pt-[7px] pb-4 px-0 w-full ipad:justify-end rtl:justify-end ${headerClassName || ""}`}
          >
            {emergencyServiceData && emergencyServiceData.length > 0 && (
              <div className="flex items-center space-x-4 ml-2 max767:w-full max767:items-center max767:ml-0">
                <img
                  src={emergencyServiceData?.[0]?.data?.icon}
                  alt={translate(
                    emergencyServiceData?.[0]?.data?.altText_en,
                    emergencyServiceData?.[0]?.data?.altText_ar
                  )}
                  className="w-8 h-8 mr-0"
                />
                <h3 className="text-lg font-semibold">
                  {translate(
                    emergencyServiceData?.[0]?.data?.title_en,
                    emergencyServiceData?.[0]?.data?.title_ar
                  )}
                </h3>
              </div>
            )}
            {title && (
              <h2 className="title text-[#48535B] text-2xl not-italic font-semibold leading-8 tracking-[-0.24px] rtl:text-right rtl:w-full ">
                {title || ""}
              </h2>
            )}
            {!hideClosebtn && (
              <button
                className="sticky top-0 right-0 text-gray-500 hover:text-gray-700 max767:bg-[#E5E9EB] max767:w-9 max767:h-9 max767:text-center max767:p-[5px] max767:rounded-[20px]"
                onClick={handleClose}
              >
                <img src={closemodal} alt="Close icon" />
              </button>
            )}
          </div>

          {children}
        </div>
      </div>
    </div>
  );
};

export default BackdropModal;
